import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import SearchableSelect from "./SearchableSelect";
import { EyeOff, Eye } from "lucide-react";

let _cache = null;
let _inflight = null;

async function loadVehicles() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api.get("/vehicles").then(({ data }) => {
    _cache = data || [];
    _inflight = null;
    return _cache;
  }).catch(() => {
    _inflight = null;
    return [];
  });
  return _inflight;
}

export function invalidateVehicleCache() { _cache = null; }

/**
 * VehiclePicker — searchable vehicle dropdown.
 * Default: hides "abgemeldete" (status === "inaktiv") vehicles. The currently
 * selected value remains visible even when inactive, so editing historic
 * records keeps working. A footer toggle reveals inactive vehicles on demand.
 */
export default function VehiclePicker({
  value,
  onChange,
  placeholder = "— Fahrzeug wählen —",
  testid = "vehicle-picker",
  disabled = false,
  filter,
  includeInactive = false,
}) {
  const [vehs, setVehs] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  useEffect(() => { loadVehicles().then(setVehs); }, []);

  const effectiveShowInactive = includeInactive || showInactive;
  const inactiveCount = useMemo(() =>
    vehs.filter((v) => v.status === "inaktiv" && (filter ? filter(v) : true)).length,
    [vehs, filter]
  );

  const options = vehs
    .filter((v) => effectiveShowInactive || v.status !== "inaktiv" || v.id === value)
    .filter((v) => (filter ? filter(v) : true))
    .map((v) => ({
      value: v.id,
      label: v.license_plate || "(ohne Kennzeichen)",
      hint: v.status === "inaktiv"
        ? "abgemeldet"
        : [v.brand, v.model].filter(Boolean).join(" "),
    }));

  const footer = inactiveCount > 0 && !includeInactive ? (
    <button
      type="button"
      onClick={() => setShowInactive((s) => !s)}
      data-testid={`${testid}-toggle-inactive`}
      className="w-full inline-flex items-center justify-center gap-2 px-2 py-1 text-[11px] rounded text-zinc-300 hover:bg-zinc-800 hover:text-white transition"
    >
      {showInactive ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
      {showInactive
        ? `Abgemeldete ausblenden (${inactiveCount})`
        : `Auch abgemeldete anzeigen (${inactiveCount})`}
    </button>
  ) : null;

  return (
    <SearchableSelect
      options={options}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      testid={testid}
      disabled={disabled}
      footer={footer}
    />
  );
}
